import {
  Building2,
  Landmark,
  GitBranch,
  Circle,
  Layers,
  Globe2,
} from "lucide-react";

const LEVEL_REGEX = /^level_(\d+)$/;

export const getLevelNumber = (unitLevel) => {
  const match = LEVEL_REGEX.exec(unitLevel || "");
  return match ? Number(match[1]) : null;
};

export const isLevelAbove = (unitLevel, threshold) => {
  const level = getLevelNumber(unitLevel);
  return level !== null && level > threshold;
};

export const formatOrgStatusLabel = (status) => {
  const value = status || "active";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

// Walks the flat org-units list (each with a parentId) to collect every
// descendant of rootId, excluding already-deleted units — mirrors the
// non-deleted subtree the API cascades to inactive on Deactivate.
export const collectDescendantUnits = (items = [], rootId) => {
  if (!rootId) return [];
  const childrenByParent = new Map();
  items.forEach((item) => {
    const parentId = item?.parentId ? String(item.parentId) : null;
    if (!parentId) return;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(item);
  });

  const collected = [];
  const seen = new Set();
  const pending = [String(rootId)];

  while (pending.length > 0) {
    const currentId = pending.shift();
    const children = childrenByParent.get(currentId) || [];
    children.forEach((child) => {
      if (!child?.id || seen.has(child.id) || child.status === "deleted")
        return;
      seen.add(child.id);
      collected.push(child);
      pending.push(String(child.id));
    });
  }

  return collected;
};

// Walks parentId up from a unit to see if any ancestor is currently inactive —
// mirrors the API's block on activating a unit under an inactive parent.
export const hasInactiveAncestor = (unitsById, parentId) => {
  let currentParentId = parentId ? String(parentId) : null;
  // Guards against a corrupted parentId chain looping back on itself — see
  // the matching comment on the API's copy of this function.
  const visited = new Set();
  while (currentParentId) {
    if (visited.has(currentParentId)) break;
    visited.add(currentParentId);

    const parent = unitsById?.get?.(currentParentId);
    if (!parent) break;
    if (parent.status === "inactive") return true;
    currentParentId = parent.parentId ? String(parent.parentId) : null;
  }
  return false;
};

const LEVEL_STYLES = [
  { color: "text-sky-600", icon: Building2 },
  { color: "text-emerald-600", icon: Landmark },
  { color: "text-amber-600", icon: GitBranch },
  { color: "text-indigo-600", icon: Circle },
];

export const ORGANIZATION_TYPE_META = {
  parent_company: {
    label: "Parent Company",
    color: "text-indigo-600",
    icon: Building2,
  },
  company: {
    label: "Company",
    color: "text-indigo-600",
    icon: Building2,
  },
  country: {
    label: "Country",
    color: "text-emerald-600",
    icon: Globe2,
  },
};

const LEVEL_LABELS = {
  1: "Business Unit",
  2: "Company",
  3: "Site",
};

const getLevelMeta = (level) => {
  const style =
    LEVEL_STYLES[(level - 1) % LEVEL_STYLES.length] || LEVEL_STYLES[0];
  return {
    label: LEVEL_LABELS[level] || `Level ${level}`,
    color: style.color,
    icon: style.icon,
  };
};

export const getUnitTypeMeta = (type) =>
  ORGANIZATION_TYPE_META[type] ||
  (() => {
    const match = LEVEL_REGEX.exec(type || "");
    if (match) {
      return getLevelMeta(Number(match[1]));
    }
    return {
      label: type,
      color: "text-slate-500",
      icon: Circle,
    };
  })();

export const flattenOrgTree = (tree = []) => {
  const collected = [];

  const visit = (nodes, pathNames = [], level1Code = "") => {
    for (const node of nodes) {
      const isGroup = Boolean(node.isGroup);
      const nextLevel1Code =
        node.unitLevel === "level_1" && node.code ? node.code : level1Code;

      if (!isGroup && node.id) {
        collected.push({
          ...node,
          level1Code: nextLevel1Code,
          parentName: pathNames.at(-1) ?? "",
          pathLabel: pathNames.join(" > "),
        });
      }

      const skipFromPath = isGroup || node.unitLevel === "parent_company";
      visit(
        node?.children ?? [],
        skipFromPath ? pathNames : [...pathNames, node.name],
        nextLevel1Code,
      );
    }
  };

  visit(tree);
  return uniqueById(collected);
};

export const disambiguateOrgDisplayNames = (orgs = []) => {
  const groups = new Map();
  orgs.forEach((org, index) => {
    const key =
      org?.name?.trim?.().toLowerCase?.() || org?.id || `org-${index}`;
    const group = groups.get(key);
    if (group) group.push(org);
    else groups.set(key, [org]);
  });

  const displayNameById = new Map();
  groups.forEach((group) => {
    const labels = buildGroupLabels(group);
    group.forEach((org) => {
      displayNameById.set(org.id, labels.get(org.id) ?? org.name);
    });
  });

  return orgs.map((org) => ({
    ...org,
    displayName: displayNameById.get(org.id) ?? org.name,
  }));
};

const buildGroupLabels = (group) => {
  if (group.length <= 1) {
    return new Map([[group[0]?.id, group[0]?.name ?? ""]]);
  }

  const maxDepth = Math.max(
    ...group.map((org) => getContextSegments(org).length),
  );

  for (let depth = 1; depth <= maxDepth; depth++) {
    const labels = new Map(
      group.map((org) => [
        org.id,
        formatLabel(org, getContextSegments(org).slice(0, depth)),
      ]),
    );
    const values = [...labels.values()];
    if (new Set(values).size === values.length) return labels;
  }

  return new Map(group.map((org) => [org.id, org.name ?? ""]));
};

const formatLabel = (org, segments) => {
  const context = segments.join(" / ");
  return context ? `${org.name} (${context})` : org.name;
};

const uniqueById = (items = []) => {
  const seen = new Set();
  return items.filter(
    (item) => item?.id && !seen.has(item.id) && seen.add(item.id),
  );
};

const getContextSegments = (org) => {
  return [org?.level1Code, org?.parentName, org?.pathLabel, org?.code].filter(
    (s) => s?.trim(),
  );
};
